import { useEffect, useRef } from 'react';

/**
 * Scrolls the referenced element into view when it becomes the deep-linked event.
 *
 * Delays the scroll by 500ms to ensure the DOM has fully rendered before scrolling.
 * Used by both the new grid-based cards (via CalendarGridStore) and the legacy
 * timeline cards (via useViewEventOnCalendar).
 *
 * @param isDeepLinkedEvent - Whether the referenced element is the deep-linked event.
 * @param elementRef - Ref to the element to scroll into view.
 * @param onScrolled - Optional callback invoked after the scroll is initiated.
 */
export const useScrollDeepLinkedEventIntoView = ({
  isDeepLinkedEvent,
  elementRef,
  onScrolled,
}: {
  isDeepLinkedEvent: boolean;
  elementRef: React.RefObject<HTMLElement | null>;
  onScrolled?: () => void;
}) => {
  // Keep a stable ref to the callback so that changes to the function reference
  // across renders do not re-trigger the scroll effect.
  const onScrolledRef = useRef(onScrolled);
  onScrolledRef.current = onScrolled;

  useEffect(() => {
    if (isDeepLinkedEvent) {
      // Delay to ensure DOM is rendered
      const timeoutId = setTimeout(() => {
        elementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        onScrolledRef.current?.();
      }, 500);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [isDeepLinkedEvent, elementRef]);
};
